import React, { type FC } from 'react';
import { observer } from 'mobx-react-lite';
import { Space, Text } from '@g2u/ui-kit';
import { Tag, Tooltip, Divider } from 'antd';
import { format, parse } from 'date-fns';
import { useQuery } from 'react-query';

import { CustomerNotes, type JsonDataItem, type WithOptionalDataTest } from 'types';
import { getCustomerNotes } from 'queries';
import { Toast, parseErrors } from 'helpers';
import styles from './customer-modal-title.module.scss';

export namespace CustomerModalTitle {
  export type Props = {
    customerId: number;
    notes?: string;
  } & WithOptionalDataTest;

  export const $: FC<Props> = observer((props) => {
    const { dataTestId, customerId, notes } = props;

    const { data: customerNotes } = useQuery(['customer-notes', { id: customerId }], getCustomerNotes, {
      enabled: !!customerId,
      onError: (error: JsonDataItem) => {
        Toast.show({ description: parseErrors(error).join(', '), type: Toast.Type.Error });
      },
    });

    const managementNotes = customerNotes?.data.filter(
      (note: JsonDataItem) => note.note_type.ID === CustomerNotes.ManagementNote,
    );

    return (
      <div className={styles.customerModalTitle} data-testid={dataTestId}>
        <div className={styles.customerModalTitle__text}>Customer File {customerId}</div>
        {notes && (
          <Tooltip
            placement="bottomLeft"
            styles={{ root: { maxWidth: 'min(640px, 70%)', width: 'max-content', minWidth: '140px' } }}
            title={
              <Space.$ direction={Space.Direction.Vertical} size={Space.Size.Middle}>
                {notes && (
                  <div>
                    <Text.$ strong style={{ color: 'white' }}>
                      Special Note:
                    </Text.$>
                    <div>{notes}</div>
                  </div>
                )}

                {managementNotes?.length > 0 && (
                  <div>
                    <Text.$ strong style={{ color: 'white' }}>
                      Management Note:
                    </Text.$>
                    {managementNotes.map((note: JsonDataItem, i: number) => (
                      <Space.$ key={note.ID} direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
                        <Text.$ className={styles.customerModalTitle__date} noMargin>
                          {format(parse(note.datetime, 'yyyy-MM-dd HH:mm:ss', new Date()), 'dd MMM yyyy')}
                        </Text.$>
                        {note.notes}
                        {i + 1 !== managementNotes.length && (
                          <Divider dashed style={{ borderColor: 'white', margin: '12px 0', width: 200 }} />
                        )}
                      </Space.$>
                    ))}
                  </div>
                )}
              </Space.$>
            }
          >
            <Tag color="error" className={styles.customerModalTitle__tag}>
              {notes}
            </Tag>
          </Tooltip>
        )}
      </div>
    );
  });
}
